import { create } from 'zustand';
import { docsApi, Document } from '../services/api';
import * as db from '../services/db';

interface StorageInfo {
  usage: number;
  quota: number;
  percentUsed: number;
}

interface DocsState {
  // Data
  documents: Document[] | null;
  currentDocument: Document | null;
  folders: string[];
  tags: string[];

  // Status
  isOnline: boolean;
  isLoading: boolean;
  error: string | null;
  lastSync: Date | null;
  storageWarning: boolean;
  storageInfo: StorageInfo | null;

  // Actions
  initialize: () => Promise<void>;
  fetchDocuments: (params?: { folder?: string; tag?: string; search?: string }) => Promise<void>;
  fetchDocumentBySlug: (slug: string) => Promise<void>;
  syncAllDocuments: () => Promise<void>;
  searchDocuments: (query: string) => Document[];
  setOnlineStatus: (online: boolean) => void;
  checkStorage: () => Promise<void>;
  dismissStorageWarning: () => void;
}

export const useDocsStore = create<DocsState>((set, get) => ({
  // Initial state
  documents: null,
  currentDocument: null,
  folders: [],
  tags: [],
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  isLoading: false,
  error: null,
  lastSync: null,
  storageWarning: false,
  storageInfo: null,

  /**
   * Initialize the store - load from IndexedDB and sync if online
   */
  initialize: async () => {
    console.log('[Store] Initializing...');
    
    try {
      // Load cached data from IndexedDB
      const [cachedDocs, cachedFolders, cachedTags, lastSync] = await Promise.all([
        db.getAllDocuments(),
        db.getFolders(),
        db.getTags(),
        db.getLastSync(),
      ]);

      set({
        documents: cachedDocs.length > 0 ? cachedDocs : null,
        folders: cachedFolders,
        tags: cachedTags,
        lastSync,
      });

      console.log(`[Store] Loaded ${cachedDocs.length} documents from cache`);

      // Check storage quota
      await get().checkStorage();

      // If online, sync in the background
      if (get().isOnline) {
        console.log('[Store] Online - starting background sync');
        get().syncAllDocuments().catch((error) => {
          console.error('[Store] Background sync failed:', error);
        });
      } else {
        console.log('[Store] Offline - using cached data only');
      }
    } catch (error) {
      console.error('[Store] Initialization failed:', error);
    }
  },

  /**
   * Fetch documents with optional filters
   * Network first, fallback to IndexedDB if offline
   */
  fetchDocuments: async (params) => {
    set({ isLoading: true, error: null });

    try {
      if (get().isOnline) {
        // Try API call
        const response = await docsApi.getAll(params);
        const docs = response.data;

        // Save to IndexedDB
        await db.saveDocuments(docs);

        set({ documents: docs, isLoading: false });
        console.log(`[Store] Fetched ${docs.length} documents from API`);
      } else {
        // Offline: load from IndexedDB
        const cachedDocs = await db.getAllDocuments();
        
        // Apply local filtering if params provided
        let filteredDocs = cachedDocs;
        if (params?.folder) {
          filteredDocs = filteredDocs.filter((doc) => doc.folder_path === params.folder);
        }
        if (params?.tag) {
          filteredDocs = filteredDocs.filter((doc) => doc.tags?.includes(params.tag!));
        }
        if (params?.search) {
          const query = params.search.toLowerCase();
          filteredDocs = filteredDocs.filter(
            (doc) =>
              doc.title.toLowerCase().includes(query) ||
              doc.content?.toLowerCase().includes(query)
          );
        }

        set({ documents: filteredDocs, isLoading: false });
        console.log(`[Store] Loaded ${filteredDocs.length} documents from cache (offline)`);
      }
    } catch (error: any) {
      console.error('[Store] Failed to fetch documents:', error);
      
      // If online but API failed, fallback to cache
      if (get().isOnline) {
        console.log('[Store] API failed, falling back to cache');
        const cachedDocs = await db.getAllDocuments();
        set({ documents: cachedDocs, isLoading: false, error: 'Using cached data' });
      } else {
        set({ error: 'Failed to load documents', isLoading: false });
      }
    }
  },

  /**
   * Fetch a single document by slug
   * Network first, fallback to IndexedDB if offline
   */
  fetchDocumentBySlug: async (slug: string) => {
    set({ isLoading: true, error: null, currentDocument: null });

    try {
      if (get().isOnline) {
        // Try API call
        const response = await docsApi.getBySlug(slug);
        const doc = response.data;

        // Save to IndexedDB
        await db.saveDocument(doc);

        set({ currentDocument: doc, isLoading: false });
        console.log(`[Store] Fetched document: ${slug} from API`);
      } else {
        // Offline: load from IndexedDB
        const cachedDoc = await db.getDocument(slug);
        
        if (cachedDoc) {
          set({ currentDocument: cachedDoc, isLoading: false });
          console.log(`[Store] Loaded document: ${slug} from cache (offline)`);
        } else {
          set({ 
            currentDocument: null, 
            isLoading: false, 
            error: 'Document not found' 
          });
        }
      }
    } catch (error: any) {
      console.error(`[Store] Failed to fetch document ${slug}:`, error);
      
      // If it's a 404, don't fallback to cache - the document is truly not accessible
      if (error.response?.status === 404) {
        set({ currentDocument: null, isLoading: false, error: 'Document not found' });
        return;
      }
      
      // For other errors (network issues, etc), fallback to cache if online
      if (get().isOnline) {
        console.log('[Store] API failed, falling back to cache');
        const cachedDoc = await db.getDocument(slug);
        
        if (cachedDoc) {
          set({ currentDocument: cachedDoc, isLoading: false, error: null });
        } else {
          set({ currentDocument: null, isLoading: false, error: 'Document not found' });
        }
      } else {
        set({ currentDocument: null, error: 'Document not found', isLoading: false });
      }
    }
  },

  /**
   * Sync all documents from API to IndexedDB
   * Only call when online
   */
  syncAllDocuments: async () => {
    if (!get().isOnline) {
      console.log('[Store] Cannot sync while offline');
      return;
    }

    console.log('[Store] Starting full sync...');

    try {
      // Fetch all data from API
      const [docsResponse, foldersResponse, tagsResponse] = await Promise.all([
        docsApi.getAll({}),
        docsApi.getFolders(),
        docsApi.getTags(),
      ]);

      const docs = docsResponse.data;
      const folders = foldersResponse.data;
      const tags = tagsResponse.data;

      // Save to IndexedDB
      await Promise.all([
        db.saveDocuments(docs),
        db.saveFolders(folders),
        db.saveTags(tags),
        db.setLastSync(new Date()),
      ]);

      // Update store
      set({
        documents: docs,
        folders,
        tags,
        lastSync: new Date(),
      });

      console.log(`[Store] Sync complete: ${docs.length} documents, ${folders.length} folders, ${tags.length} tags`);

      // Check storage after sync
      await get().checkStorage();
    } catch (error) {
      console.error('[Store] Sync failed:', error);
      throw error;
    }
  },

  /**
   * Search documents locally (for offline use)
   */
  searchDocuments: (query: string) => {
    const docs = get().documents || [];
    
    if (!query.trim()) {
      return docs;
    }

    const searchTerm = query.toLowerCase();
    
    return docs.filter(
      (doc) =>
        doc.title.toLowerCase().includes(searchTerm) ||
        doc.content?.toLowerCase().includes(searchTerm) ||
        doc.folder_path.toLowerCase().includes(searchTerm)
    );
  },

  /**
   * Update online status
   */
  setOnlineStatus: (online: boolean) => {
    const wasOffline = !get().isOnline;
    set({ isOnline: online });
    
    console.log(`[Store] Online status changed: ${online ? 'ONLINE' : 'OFFLINE'}`);

    // If we just came back online, sync
    if (online && wasOffline) {
      console.log('[Store] Back online - syncing...');
      get().syncAllDocuments().catch((error) => {
        console.error('[Store] Sync after reconnection failed:', error);
      });
    }
  },

  /**
   * Check storage quota and set warning if needed
   */
  checkStorage: async () => {
    try {
      const storageInfo = await db.checkStorageQuota();
      
      set({ storageInfo });

      // Warn if over 80% used
      if (storageInfo.percentUsed > 80) {
        set({ storageWarning: true });
        console.warn(`[Store] Storage warning: ${storageInfo.percentUsed.toFixed(1)}% used`);
      }
    } catch (error) {
      console.error('[Store] Failed to check storage:', error);
    }
  },

  /**
   * Dismiss storage warning (only for current session)
   */
  dismissStorageWarning: () => {
    set({ storageWarning: false });
    console.log('[Store] Storage warning dismissed');
  },
}));
